import "./KeywordDashboard.scss";
import axios from "axios";
import { BASE_URL } from "../../api/api";
import { useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { getURLParams } from "../../utils/helper";
import LineChart from "../../components/LineChart/LineChart";
import BarChart from "../../components/BarChart/BarChart";
import Spin from "../../components/Spin/Spin";
import Overview from "../../components/Overview/Overview";
import Posts from "../../components/Posts/Posts";
import Recommendation from "../../components/Recommendation/Recommendation";
import ErrorBoundary from "../../components/ErrorBoundary/ErrorBoundary";
import ErrorFallback from "../../components/ErrorFallback/ErrorFallback";

const DashboardContent = ({
  timeInterval,
  postsAnalyzed,
  subreddits,
  setAnalysis,
}) => {
  const [keywordData, setKeywordData] = useState({
    barChartTerms: {
      uniGrams: {},
      biGrams: {},
      triGrams: {},
      srCount: {},
    },
    lineChartData: {},
    medianComments: 0,
    avgComments: 0,
    numAnalyzed: 0,
    top3Posts: [],
  });

  const [error, setError] = useState(false);
  const history = useHistory();
  const { kw: keyword } = getURLParams(history.location.search);

  const description = `Analysed the top ${
    keywordData.numAnalyzed
  } posts including the keyword: ${
    keyword[0].toUpperCase() + keyword.slice(1)
  } over ${
    timeInterval.includes("all")
      ? `all time`
      : `the last ${timeInterval.toLowerCase()}`
  }. Top posts are defined as those posts with the largest number of upvotes within the specified time interval.`;

  useEffect(() => {
    const getKeywordData = async () => {
      setError(false);
      setKeywordData({
        barChartTerms: {
          uniGrams: {},
          biGrams: {},
          triGrams: {},
          srCount: {},
        },
        lineChartData: {},
        medianComments: 0,
        avgComments: 0,
        numAnalyzed: 0,
        top3Posts: [],
      });
      try {
        const sid = window.sessionStorage.getItem("sid");
        const { t, n, kw } = getURLParams(history.location.search);

        const res = await axios.post(`${BASE_URL}/reddit/keyword`, {
          keyword: kw,
          nPosts: n,
          limit: t,
          sid: sid,
        });

        if (res.data.numAnalyzed === 0) {
          setError(true);
          return;
        }

        setKeywordData(res.data);
      } catch (err) {
        console.log("Axios GET keyword error", err);
        setError(true);
      }
    };

    // on mount
    getKeywordData();
    document.title = `Eureka | Analysis for k/${keyword}`;
    setAnalysis(true);
  }, [keyword, setAnalysis, history.location.search]);

  if (error) return <ErrorFallback searchTerm={keyword} />;
  else if (Object.keys(keywordData.lineChartData).length === 0) return <Spin />;
  else {
    return (
      <main className="kwdashboard">
        <Overview
          subscribers={keywordData.avgComments.toFixed(2)}
          subredditPulse={keywordData.medianComments}
          description={description}
          searchTerm={keyword}
        />
        <Recommendation
          recs={keywordData.barChartTerms.srCount}
          subreddits={subreddits}
          kw={true}
          timeInterval={timeInterval}
          postsAnalyzed={postsAnalyzed}
        />
        {/* top  */}
        <section className="kwdashboard__stats">
          <article className="kwdashboard__top">
            <div className="kwdashboard__title-container">
              <h2 className="kwdashboard__title">Data Analysis</h2>
            </div>
            <div className="kwdashboard__top-container">
              <LineChart
                keyword={keyword}
                time={timeInterval}
                postsAnalyzed={postsAnalyzed}
                data={keywordData.lineChartData}
              />
            </div>
          </article>
          {/* bottom */}
          <article className="kwdashboard__bottom">
            <div className="ngrams__top">
              <BarChart
                topN={5}
                barChartData={keywordData.barChartTerms.srCount}
                title={`Top Subreddits Talking About ${
                  keyword[0].toUpperCase() + keyword.slice(1)
                }`}
                seriesName={"% of Top Posts"}
              />
              <BarChart
                barChartData={keywordData.barChartTerms.uniGrams}
                horizontal={true}
                title={`Most Common Terms in Post Titles`}
                xLabel={"Word Frequency"}
                yLabel={"Word"}
                seriesName="Word count"
                topN={10}
              />
            </div>
            <div className="ngrams__bottom">
              <BarChart
                barChartData={keywordData.barChartTerms.biGrams}
                horizontal={true}
                title={`Most Common BiGrams in Post Titles`}
                xLabel={"BiGram Frequency"}
                yLabel={"BiGram"}
                seriesName="Word count"
                topN={10}
              />
              <BarChart
                barChartData={keywordData.barChartTerms.triGrams}
                horizontal={true}
                title={`Most Common TriGrams in Post Titles`}
                xLabel={"TriGram Frequency"}
                yLabel={"TriGram"}
                seriesName="Word count"
                topN={10}
              />
            </div>
          </article>
        </section>
        <Posts
          topPosts={keywordData.top3Posts}
          subreddits={subreddits}
          keyword={keyword}
        />
      </main>
    );
  }
};

const KeywordDashboard = (Props) => {
  const history = useHistory();
  const { kw } = getURLParams(history.location.search);
  return (
    <ErrorBoundary fallback={<ErrorFallback searchTerm={kw} />}>
      <DashboardContent
        timeInterval={Props.timeInterval}
        setTimeInterval={Props.setTimeInterval}
        subreddits={Props.subreddits}
        setAnalysis={Props.setAnalysis}
        postsAnalyzed={Props.postsAnalyzed}
      />
    </ErrorBoundary>
  );
};

export default KeywordDashboard;
